import type { Editor } from '@tiptap/react';
import { useCallback } from 'react';
import type { ComposerDictationPorts } from './composer-dictation-bar';

export interface ComposerDictation {
  /** Listening or transcribing a dictation started from this composer's mic. */
  readonly isActive: boolean;
  readonly isListening: boolean;
  readonly isProcessing: boolean;
  /** Abort without transcribing. Only while listening. */
  readonly cancel: () => void;
  /** Stop and transcribe. The shared dictate control inserts the transcript. */
  readonly submit: () => void;
}

const swallow = () => undefined;

/**
 * Observes the shared dictation store for the session this composer's
 * `DictateButton` started. The button writes `source` and `editor` on start,
 * so a session is ours when the store's editor is the composer's editor; a
 * dictation started from another surface leaves this composer idle.
 *
 * Insertion stays with the button (it watches the same store), so this hook
 * only stops or cancels the session.
 */
export const useComposerDictation = (
  editor: Editor | null,
  getEditor: (() => Editor | null) | undefined,
  ports: ComposerDictationPorts
): ComposerDictation => {
  const status = ports.useDictationStore((s) => s.status);
  const source = ports.useDictationStore((s) => s.source);
  const sessionEditor = ports.useDictationStore((s) => s.editor);
  const stop = ports.useDictationStore((s) => s.stop);
  const cancel = ports.useDictationStore((s) => s.cancel);
  const reset = ports.useDictationStore((s) => s.reset);

  const composerEditor = getEditor?.() ?? editor;
  const ownsSession =
    source === 'webDictateButton' &&
    composerEditor !== null &&
    sessionEditor === composerEditor;
  const isListening = ownsSession && status === 'LISTENING';
  const isProcessing = ownsSession && status === 'PROCESSING';

  const { isNativeHost, stopNativeDictation, cancelNativeDictation } = ports;

  const submit = useCallback(() => {
    if (!isListening) return;
    if (isNativeHost()) void stopNativeDictation().catch(swallow);
    else void stop();
  }, [isListening, stop, isNativeHost, stopNativeDictation]);

  // Web: the store's cancel() parks the session in ERROR/CANCELLED so the
  // shared dictation bubble can float its "Cancelled" pill. This row is its own
  // confirmation (it collapses back to the mic), so settle the store to idle in
  // the same tick, exactly as the bubble's own Escape-on-error does. Scoped to
  // this composer's session; every other DictateButton surface keeps the pill.
  // Native: Rust's dictation:result already returns a button session to idle.
  const handleCancel = useCallback(() => {
    if (!isListening) return;
    if (isNativeHost()) {
      void cancelNativeDictation().catch(swallow);
      return;
    }
    cancel();
    reset();
  }, [isListening, cancel, reset, isNativeHost, cancelNativeDictation]);

  return {
    isActive: isListening || isProcessing,
    isListening,
    isProcessing,
    cancel: handleCancel,
    submit,
  };
};
